import { useQuery } from "@hooks/useQuery";
import { Button, Form, Switch, TableColumnsType } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { useNotification } from "~/Hooks/useNotification";
import { useDocumentDetails } from "~/Modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { eramanBEApi, post, put } from "~/Services";
import { eramanApiURL } from "~/Utils";
import EmailsColumn from "./EmailsColumn";

export const NotifcationSettings = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>("name");
  const [button, setButton] = useState<"save" | "submit">("save");
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const { openToast } = useNotification();
  const [emailErrors, setEmailErrors] = useState<Record<number, boolean>>({}); // key=rowId

  const updateEmailError = (rowId: number, hasError: boolean) => {
    setEmailErrors((prev) => ({ ...prev, [rowId]: hasError }));
  };

  const hasAnyEmailError = Object.values(emailErrors).some(Boolean);

  const {
    data: dataById,
    isLoading: isLoadingData,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const getRecords = () => {
    setIsLoading(true);
    let url = `/admin/notifications`;
    eramanBEApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(res.data.totalRows);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const id = query.get("id");
    if (id) {
      refetchDocument();
    } else {
      getRecords();
    }
  }, [query.get("id")]);

  useEffect(() => {
    if (query.get("id")) {
      if (dataById?.payload) {
        setData(dataById.payload);
        setTotalRecords(dataById.payload.length || 0);
      }
    }
  }, [dataById]);

  const handleUpdateNotification = (id: number, key: string, value: any) => {
    const updated = data.map((item: any) => {
      if (item.id === id) {
        return { ...item, [key]: value };
      }
      return item;
    });
    setData(updated);
  };

  const handleFormSubmit = () => {
    // Auto-add any valid emails that are still in input fields
    const updatedData = data.map((item: any) => {
      const inputElement = document.getElementById(`new-email-${item.id}`) as HTMLInputElement;
      if (inputElement && inputElement.value.trim()) {
        const newEmail = inputElement.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(newEmail)) {
          const currentEmails = item.emails ? item.emails.split(",").filter((e: any) => e.trim()) : [];
          if (!currentEmails.includes(newEmail)) {
            currentEmails.push(newEmail);
            // Clear the input field after adding
            inputElement.value = "";
            return { ...item, emails: currentEmails.join(",") };
          }
        }
      }
      return item;
    });

    setData(updatedData);

    if (hasAnyEmailError) {
      return;
    }
    setConfirmModalVisible(true);
  };

  const saveUpdates = () => {
    const url = `${eramanApiURL}/admin/notifications`;
    axios
      .put(url, data)
      .then(() => {
        console.log(`Notifications updated successfully`);
        setIsLoading(true);
        getRecords();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    let imageKeys: any = [
      "fbImage",
      "field_purchase_login_pop_image",
      "field_login_image",
      "filledImage",
      "googleImage",
      "showImage",
      "field_logout_image",
      "field_intro_details_web_image",
      "field_video_url",
    ];

    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field && field[0]) {
          if (field[0].originFileObj) {
            const formData = new FormData();
            formData.append("upload-file", field[0].originFileObj);
            formData.append("application", "ERAMAN");
            const res: any = await post("management/upload-file", formData);

            if (res) {
              data[key] = res.response.data;
            }
          } else {
            data[key] = data[key][0].url;
          }
        }
      } catch (error) {}
    }

    let descKeys: any = [
      "field_introduction_description",
      "field_intro_description_malay",
      "field_intro_description_mandarin",
    ];
    for (const key of descKeys) {
      try {
        data[key] = {
          value: data[key] || "",
          format: "full_html",
        };
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "Configuration",
      subModule: `notificationSettings`,
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };
    console.log(payload, "payload");
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  const handleConfirmSubmit = async (reviewNotes: string) => {
    try {
      await onSubmit(
        data,
        `${eramanApiURL}/admin/notifications`,
        "UPDATE",
        "PUT",
        button === "save" ? "DRAFT" : "PENDING",
        reviewNotes
      );
    } catch (err) {
      // TODO: Handle Error
      console.error("Error during submission:", err);
    }
  };

  const columns: TableColumnsType = [
    {
      title: "Notification Type",
      dataIndex: "notificationType",
      width: 120,
      key: "notificationType",
      render: (_: any, record: any) => (
        <>
          <strong>{record.name}</strong>
          <p>{record.description}</p>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "enabled",
      key: "enabled",
      width: 30,
      render: (_: any, record: any) => (
        <Switch
          checked={record.enabled === "True"}
          onChange={(checked) => handleUpdateNotification(record.id, "enabled", checked ? "True" : "False")}
        />
      ),
    },
    {
      title: "Emails",
      dataIndex: "emails",
      key: "emails",
      render: (_: any, record: any) => (
        <EmailsColumn
          // emails={record?.emails?.split(",")}
          rowId={record.id}
          emails={record?.emails ? record.emails.split(",").filter((e: any) => e.trim()) : []}
          onChange={(newEmails: string[]) => handleUpdateNotification(record.id, "emails", newEmails.join(","))}
          onErrorChange={(hasError) => updateEmailError(record.id, hasError)}
        />
      ),
    },
  ];

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns}
        className="table-clickable"
        tableData={
          data
            ? data?.map((d: any) => {
                return {
                  ...d,
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        multiSelect={false}
        hideExport={true}
        showTopBar={false}
      />
      <div className="flex justify-end mt-3">
        <Form.Item className="col-span-12 gap-5">
          <div className={"flex gap-5 flex-row-reverse"}>
            <Button
              type="primary"
              htmlType="submit"
              // disabled={hasAnyEmailError}
              onClick={(e) => {
                setButton("submit");
                handleFormSubmit();
              }}
            >
              Submit for Review
            </Button>
            <Button
              type="ghost"
              htmlType="submit"
              onClick={(e) => {
                setButton("save");
                handleFormSubmit();
              }}
            >
              Save as Draft
            </Button>
          </div>
        </Form.Item>
        {/* <Button type="primary"  onClick={saveUpdates}>Save</Button> */}
      </div>
      <ConfirmationModal
        open={confirmModalVisible}
        onClose={() => setConfirmModalVisible(false)}
        onSubmit={handleConfirmSubmit}
        type={button}
      />
    </>
  );
};
